import React from 'react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import axios from 'axios';
import moment from 'moment';
import { toast } from 'react-toastify';
import { utils, write, book_new, book_append_sheet } from "xlsx";

const MasterReports = () => {
    const storedToken = localStorage.getItem("token");

    const exportEmployeeReport = async () => {
        try {
            const path = "/getEmployeeMasterReport";
            const URL = process.env.REACT_APP_BE_URL + path;
            const response = await axios.get(URL, {
                headers: {
                    Authorization: `Bearer ${storedToken}`,
                },
            });

            if (response.status === 200) {
                const allEmployees = [
                    ...response.data.onboardEmployeeDetails,
                    ...response.data.offboardEmployeeDetails,
                ];

                const worksheet = XLSX.utils.json_to_sheet(allEmployees);
                const workbook = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(workbook, worksheet, "Employees");
                const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
                const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });

                const today = new Date();
                const year = today.getFullYear().toString().slice(-2);
                const month = (today.getMonth() + 1).toString().padStart(2, '0');
                const day = today.getDate().toString().padStart(2, '0');
                const formattedDate = `${year}${month}${day}`;

                saveAs(blob, `WW360-Employees_Report_${formattedDate}.xlsx`);
            }
        } catch (error) {
            console.error("Failed to export employee report:", error);
        }
    };

    const exportProjectReport = async () => {
        try {
            const path = "/getProjectDetails";
            const URL = process.env.REACT_APP_BE_URL + path;
            const response = await axios.get(URL, {
                headers: {
                    Authorization: `Bearer ${storedToken}`,
                },
            });

            if (response.status === 200) {
                const formattedProjects = response.data.GetOnboardedProjects.map(project => {
                    const [prefix, ...numberParts] = project.ClientContactDetails.split(' ');
                    const number = numberParts.join(' ');
                    return {
                        ...project,
                        StartDate: moment(project.StartDate).format('YYYY-MM-DD'),
                        EndDate: moment(project.EndDate).format('YYYY-MM-DD'),
                        clientContactPrefix: prefix,
                        ClientContactDetails: number
                    };
                });

                const worksheet = XLSX.utils.json_to_sheet(formattedProjects);
                const workbook = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(workbook, worksheet, "Projects");
                const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
                const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });

                const today = new Date();
                const year = today.getFullYear().toString().slice(-2);
                const month = (today.getMonth() + 1).toString().padStart(2, '0');
                const day = today.getDate().toString().padStart(2, '0');
                const formattedDate = `${year}${month}${day}`;

                saveAs(blob, `WW360-Projects_Report_${formattedDate}.xlsx`);
            }
        } catch (error) {
            console.error("Failed to export project report:", error);
        }
    };

    const exportAssignProjectReport = async () => {
        try {
            const path = "/getAssignProjectDetails";
            const URL = process.env.REACT_APP_BE_URL + path;
            const response = await axios.get(URL, {
                headers: {
                    Authorization: `Bearer ${storedToken}`,
                },
            })
            if (response.status === 200) {
                const formattedProjects = response.data.listProjects.map(project => ({
                    ...project,
                }));

                const worksheet = XLSX.utils.json_to_sheet(formattedProjects);
                const workbook = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(workbook, worksheet, "AssignProjects");
                const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
                const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });

                const today = new Date();
                const year = today.getFullYear().toString().slice(-2);
                const month = (today.getMonth() + 1).toString().padStart(2, '0');
                const day = today.getDate().toString().padStart(2, '0');
                const formattedDate = `${year}${month}${day}`;

                saveAs(blob, `WW360-AssignProjects_Report_${formattedDate}.xlsx`);
            }
        } catch (error) {
            console.error("Failed to export assign project report:", error);
        }
    };

    const exportTimesheetReport = async () => {
        try {
            const token = localStorage.getItem("token");
            const path = "/viewTimesheet";
            const URL = process.env.REACT_APP_BE_URL + path;

            const response = await axios.get(URL, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            if (response.status === 200) {
                const listTimesheetDetails = response.data.listTimesheetDetails;

                if (!listTimesheetDetails.length) {
                    toast.warning("No data available for export.", {
                        position: toast.POSITION.TOP_CENTER,
                    });
                    return;
                }

                const timesheets = listTimesheetDetails.flatMap(emp =>
                    emp.timesheetSummary.map(summary => ({
                        ...summary,
                        employeeId: emp.employeeId,
                        employeeName: emp.employeeName
                    }))
                );

                // Collect all unique dates
                const allDates = Array.from(new Set(timesheets.flatMap(timesheet =>
                    timesheet.timeSheetHours.map(day => moment(day.weekDate, "YYYY-MM-DD").format("DD-MM-YYYY"))
                )));

                const headers = ["User ID", "Employee Name", "Week Number", "Start & End Date", "Total (Hours)", "Status", "Dates", "Hours", "Projects"];

                // Transform the timesheet data
                const exportData = timesheets.flatMap(timesheet => {
                    return allDates.flatMap(date => {  // Use flatMap to handle multiple projects
                        const dayData = timesheet.timeSheetHours.find(day =>
                            moment(day.weekDate, "YYYY-MM-DD").format("DD-MM-YYYY") === date
                        );

                        if (!dayData || !dayData.projectHours.length) {
                            return null; // Skip if no data
                        }

                        return dayData.projectHours.map(project => ({  // Map each project separately
                            "User ID": timesheet.employeeId,
                            "Employee Name": timesheet.employeeName,
                            "Week Number": timesheet.weekNumber,
                            "Start & End Date": `${timesheet.weekStartDate} - ${timesheet.weekEndDate}`,
                            "Total (Hours)": timesheet.totalHours,
                            "Status": timesheet.status,
                            "Dates": date,
                            "Hours": project.hours,  // Individual project hours
                            "Projects": project.projectName // Individual project name
                        }));
                    }).filter(row => row !== null); // Filter out null rows
                });

                // Create a worksheet from the transformed data
                const ws = utils.json_to_sheet(exportData, { header: headers });

                // Apply bold font to the headers
                ws['!cols'] = headers.map(() => ({ wpx: 120 })); // Adjust column width if necessary

                // Manually set styles for the header row
                ws['A1'].s = { font: { bold: true } };
                ws['B1'].s = { font: { bold: true } };
                ws['C1'].s = { font: { bold: true } };
                ws['D1'].s = { font: { bold: true } };
                ws['E1'].s = { font: { bold: true } };
                ws['F1'].s = { font: { bold: true } };
                ws['G1'].s = { font: { bold: true } };
                ws['H1'].s = { font: { bold: true } };

                // Create a new workbook and append the worksheet
                const wb = utils.book_new();
                utils.book_append_sheet(wb, ws, 'Timesheet');

                // Generate a buffer from the workbook
                const excelBuffer = write(wb, { bookType: 'xlsx', type: 'array' });

                // Create a blob from the buffer and save the file
                const data = new Blob([excelBuffer], { type: 'application/octet-stream' });
                const today = new Date();
                const year = today.getFullYear().toString().slice(-2); // Get last two digits of the year
                const month = (today.getMonth() + 1).toString().padStart(2, '0'); // Add leading 0 for month
                const day = today.getDate().toString().padStart(2, '0'); // Add leading 0 for day
                const formattedDate = `${year}${month}${day}`;
                saveAs(data, `WW360-Timesheet_Master_Report_${formattedDate}.xlsx`);
            }
        } catch (error) {
            console.error("Failed to export timesheet report:", error);
            toast.error("Failed to export report.", {
                position: toast.POSITION.TOP_CENTER,
            });
        }
    };

    const exportLeaveReport = async () => {
        try {
            const path = `/LeaveTracker/summary`;
            const URL = process.env.REACT_APP_BE_URL + path;
            const response = await axios.get(URL, {
                headers: {
                    Authorization: `Bearer ${storedToken}`
                },
            });
            if (response.status === 200) {
                const leaves = response.data?.map(leave => ({
                    ...leave,
                }));

                const worksheet = XLSX.utils.json_to_sheet(leaves);
                const workbook = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(workbook, worksheet, "Leaves");
                const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
                const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });

                const today = new Date();
                const year = today.getFullYear().toString().slice(-2);
                const month = (today.getMonth() + 1).toString().padStart(2, '0');
                const day = today.getDate().toString().padStart(2, '0');
                const formattedDate = `${year}${month}${day}`;

                saveAs(blob, `WW360-Leave_Report_${formattedDate}.xlsx`);
            }
        } catch (error) {
            console.error("Failed to export leave report:", error);
        }
    };

    const exportAssetsReport = async () => {
        try {
            const path = `/getAssetMasterReport`;
            const URL = process.env.REACT_APP_BE_URL + path;
            const response = await axios.get(URL, {
                headers: {
                    Authorization: `Bearer ${storedToken}`
                },
            });
            if (response.status === 200) {
                const Assets = response.data?.map(asset => ({
                    ...asset,
                }));

                const worksheet = XLSX.utils.json_to_sheet(Assets);
                const workbook = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(workbook, worksheet, "Assets");
                const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
                const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });

                const today = new Date();
                const year = today.getFullYear().toString().slice(-2);
                const month = (today.getMonth() + 1).toString().padStart(2, '0');
                const day = today.getDate().toString().padStart(2, '0');
                const formattedDate = `${year}${month}${day}`;

                saveAs(blob, `WW360-Asset_Report_${formattedDate}.xlsx`);
            }
        } catch (error) {
            console.error("Failed to export asset report:", error);
        }
    };

    const exportClaimsReport = async () => {
        try {
            const path = `/getSubmitClaimsDetails`;
            const URL = process.env.REACT_APP_BE_URL + path;
            const response = await axios.get(URL, {
                headers: {
                    Authorization: `Bearer ${storedToken}`
                },
            });
            if (response.status === 200) {
                const claims = response.data?.listSubmitClaims.map(claim => ({
                    ...claim,
                }));

                const worksheet = XLSX.utils.json_to_sheet(claims);
                const workbook = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(workbook, worksheet, "claims");
                const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
                const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });

                const today = new Date();
                const year = today.getFullYear().toString().slice(-2);
                const month = (today.getMonth() + 1).toString().padStart(2, '0');
                const day = today.getDate().toString().padStart(2, '0');
                const formattedDate = `${year}${month}${day}`;

                saveAs(blob, `WW360-claim_Report_${formattedDate}.xlsx`);
            }
        } catch (error) {
            console.error("Failed to export claim report:", error);
        }
    };

    const exportSupportReport = async () => {
        try {
            const path = `/getSupportAndRequests`;
            const URL = process.env.REACT_APP_BE_URL + path;
            const response = await axios.get(URL, {
                headers: {
                    Authorization: `Bearer ${storedToken}`
                },
            });
            if (response.status === 200) {
                const supportReports = response.data?.supportRequests.map(support => ({
                    ...support,
                }));

                const worksheet = XLSX.utils.json_to_sheet(supportReports);
                const workbook = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(workbook, worksheet, "Support-Request-Reports");
                const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
                const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });

                const today = new Date();
                const year = today.getFullYear().toString().slice(-2);
                const month = (today.getMonth() + 1).toString().padStart(2, '0');
                const day = today.getDate().toString().padStart(2, '0');
                const formattedDate = `${year}${month}${day}`;

                saveAs(blob, `WW360-Support-Request_Report_${formattedDate}.xlsx`);
            }
        } catch (error) {
            console.error("Failed to export Support-Request report:", error);
        }
    };

    return (
        <div className="content-page">
            <div className="container-fluid">
                <div className="row">
                    <div className="col-lg-12">
                        <div className="row mb-3">
                            <div className="col">
                                <h3 className="title mb-2">Master Reports</h3>
                            </div>
                            <div className="col-auto">
                                <a href="/SubmitTimesheet" className="btn btn-primary mt-lg-0 mt-4">
                                    <i className="ri-calendar-schedule-fill"></i> Timesheet
                                </a>
                            </div>
                        </div>
                    </div>

                    <div className="col-lg-12">
                        <div className="row">
                            {/* Employee Report Card */}
                            <div className="col-lg-4 col-md-6">
                                <div className="card dashboard-card mb-4 green" style={{ cursor: 'default' }}>
                                    <div className="card-body pt-2">
                                        <div className="d-flex align-items-top justify-content-between">
                                            <div className="icon icon-box rounded">
                                                <i className="ri-group-fill"></i>
                                            </div>
                                            <i
                                                className="ri-download-2-fill"
                                                style={{ cursor: 'pointer', fontSize: '18px' }}
                                                onClick={exportEmployeeReport}
                                            ></i>
                                        </div>
                                        <h5 className="title mb-0">Employee</h5>
                                        <small className="mb-0 text-muted">Download Report</small>
                                    </div>
                                </div>
                            </div>
                            {/* Project Report Card */}
                            <div className="col-lg-4 col-md-6">
                                <div className="card dashboard-card mb-4 blue" style={{ cursor: 'default' }}>
                                    <div className="card-body pt-2">
                                        <div className="d-flex align-items-top justify-content-between">
                                            <div className="icon icon-box rounded">
                                                <i className="ri-folder-chart-fill"></i>
                                            </div>
                                            <i
                                                className="ri-download-2-fill"
                                                style={{ cursor: 'pointer', fontSize: '18px' }}
                                                onClick={exportProjectReport}
                                            ></i>
                                        </div>
                                        <h5 className="title mb-0">Project</h5>
                                        <small className="mb-0 text-muted">Download Report</small>
                                    </div>
                                </div>
                            </div>

                            {/* Assign Project Report Card */}
                            <div className="col-lg-4 col-md-6">
                                <div className="card dashboard-card mb-4 purple" style={{ cursor: 'default' }}>
                                    <div className="card-body pt-2">
                                        <div className="d-flex align-items-top justify-content-between">
                                            <div className="icon icon-box rounded">
                                                <i className="ri-briefcase-fill"></i>
                                            </div>
                                            <i
                                                className="ri-download-2-fill"
                                                style={{ cursor: 'pointer', fontSize: '18px' }}
                                                onClick={exportAssignProjectReport}
                                            ></i>
                                        </div>
                                        <h5 className="title mb-0">Assign Project</h5>
                                        <small className="mb-0 text-muted">Download Report</small>
                                    </div>
                                </div>
                            </div>

                            {/* Timesheet Report Card */}
                            <div className="col-lg-4 col-md-6">
                                <div className="card dashboard-card mb-4 amber" style={{ cursor: 'default' }}>
                                    <div className="card-body pt-2">
                                        <div className="d-flex align-items-top justify-content-between">
                                            <div className="icon icon-box rounded">
                                                <i className="ri-time-fill"></i>
                                            </div>
                                            <i
                                                className="ri-download-2-fill"
                                                style={{ cursor: 'pointer', fontSize: '18px' }}
                                                onClick={exportTimesheetReport}
                                            ></i>
                                        </div>
                                        <h5 className="title mb-0">Timesheet</h5>
                                        <small className="mb-0 text-muted">Download Report</small>
                                    </div>
                                </div>
                            </div>

                            {/* Leave Report Card */}
                            <div className="col-lg-4 col-md-6">
                                <div className="card dashboard-card mb-4 teal" style={{ cursor: 'default' }}>
                                    <div className="card-body pt-2">
                                        <div className="d-flex align-items-top justify-content-between">
                                            <div className="icon icon-box rounded">
                                                <i className="ri-calendar-check-fill"></i>
                                            </div>
                                            <i
                                                className="ri-download-2-fill"
                                                style={{ cursor: 'pointer', fontSize: '18px' }}
                                                onClick={exportLeaveReport}
                                            ></i>
                                        </div>
                                        <h5 className="title mb-0">Leave</h5>
                                        <small className="mb-0 text-muted">Download Report</small>
                                    </div>
                                </div>
                            </div>

                            {/* Assets Report Card */}
                            <div className="col-lg-4 col-md-6">
                                <div className="card dashboard-card mb-4 orange" style={{ cursor: 'default' }}>
                                    <div className="card-body pt-2">
                                        <div className="d-flex align-items-top justify-content-between">
                                            <div className="icon icon-box rounded">
                                                <i className="ri-macbook-fill"></i>
                                            </div>
                                            <i
                                                className="ri-download-2-fill"
                                                style={{ cursor: 'pointer', fontSize: '18px' }}
                                                onClick={exportAssetsReport}
                                            ></i>
                                        </div>
                                        <h5 className="title mb-0">Assets</h5>
                                        <small className="mb-0 text-muted">Download Report</small>
                                    </div>
                                </div>
                            </div>

                            {/* Claims Report Card */}
                            <div className="col-lg-4 col-md-6">
                                <div className="card dashboard-card mb-4 red" style={{ cursor: 'default' }}>
                                    <div className="card-body pt-2">
                                        <div className="d-flex align-items-top justify-content-between">
                                            <div className="icon icon-box rounded">
                                                <i className="ri-file-list-3-fill"></i>
                                            </div>
                                            <i
                                                className="ri-download-2-fill"
                                                style={{ cursor: 'pointer', fontSize: '18px' }}
                                                onClick={exportClaimsReport}
                                            ></i>
                                        </div>
                                        <h5 className="title mb-0">Claims</h5>
                                        <small className="mb-0 text-muted">Download Report</small>
                                    </div>
                                </div>
                            </div>

                            {/* Support and request Report Card */}
                            <div className="col-lg-4 col-md-6">
                                <div className="card dashboard-card mb-4 indigo" style={{ cursor: 'default' }}>
                                    <div className="card-body pt-2">
                                        <div className="d-flex align-items-top justify-content-between">
                                            <div className="icon icon-box rounded">
                                                <i className="ri-customer-service-2-fill"></i>
                                            </div>
                                            <i
                                                className="ri-download-2-fill"
                                                style={{ cursor: 'pointer', fontSize: '18px' }}
                                                onClick={exportSupportReport}
                                            ></i>
                                        </div>
                                        <h5 className="title mb-0">Support and Request</h5>
                                        <small className="mb-0 text-muted">Download Report</small>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MasterReports;
